<template>
  <div :class="$style.container">
    <slot
      :clearSearchTerm="clearSearchTerm"
      :currentPageNum="getPageNumber"
      :getSearchTerm="getSearchTerm"
      :handleDeleteAllFilters="handleDeleteAllFilters"
      :handleDeleteFilters="handleDeleteFilters"
      :handleEnterSearchTerm="handleEnterSearchTerm"
      :handlePageChange="handlePageChange"
      :handleSelect="handleSelect"
      :handleSelectAll="handleSelectAll"
      :handleSetFilters="handleSetFilters"
      :handleSortColumn="handleSortColumn"
      :indeterminateSelectAll="areOnlySomeItemsSelected"
      :loading="loading"
      :rowsPerPage="getRowsPerPage"
      :selectAll="areAllItemsSelected"
      :selectable="selectable"
      :shouldFocusSearch="shouldFocusSearch"
      :showEmptyState="!!getSearchTerm && !getCollection.length"
      :sortBy="sortBy"
      :sortDirection="sortDirection"
      :tableData="tableData"
      :totalCount="totalCount"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import LocalStorageService from 'client/util/LocalStorageService';

const defaultPageNum = 0;
const defaultRowsPerPage = 10;

export default {
  name: 'ItemListCollectionWrapper',

  props: {
    collectionModule: {
      type: String,
      required: true,
    },

    defaultFilters: {
      type: Array,
      required: false,
      default: () => [],
    },

    defaultSortBy: {
      type: String,
      required: true,
    },

    idField: {
      type: String,
      default: 'id',
    },

    refreshKey: {
      type: Number,
      default: undefined,
    },

    rowsPerPageLocalStorageKey: {
      type: String,
      required: true,
    },

    selectable: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      loading: false,
      shouldFocusSearch: false,
    };
  },

  LocalStorageService,

  computed: {
    areAllItemsSelected() {
      return this.$store.getters[`${this.collectionModule}/areAllItemsSelected`];
    },

    areOnlySomeItemsSelected() {
      return this.$store.getters[`${this.collectionModule}/areOnlySomeItemsSelected`];
    },

    getCollection() {
      return this.$store.getters[`${this.collectionModule}/getCollection`];
    },

    getPageNumber() {
      return this.$store.getters[`${this.collectionModule}/getPage`];
    },

    getRowsPerPage() {
      return Number(this.$store.getters[`${this.collectionModule}/getRowsPerPage`]) || defaultRowsPerPage;
    },

    getSearchTerm() {
      return this.$store.getters[`${this.collectionModule}/getSearchTerm`];
    },

    getSortField() {
      return this.$store.getters[`${this.collectionModule}/getSortField`];
    },

    /**
     * A `Set` of all selected items for fast and easy lookup.
     *
     * Lookup time of `Set.has` -> `O(1)`
    */
    selectedIdsSet() {
      const selectedIds = this.$store.getters[`${this.collectionModule}/getSelectedItems`];

      return new Set(selectedIds);
    },

    sort() {
      return this.$store.state[this.collectionModule].features.items.sort;
    },

    sortBy() {
      return this.getSortField.replace('-', '');
    },

    sortDirection() {
      return this.getSortField.charAt(0) === '-' ? 'descending' : 'ascending';
    },

    tableData() {
      let items = this.getCollection;

      if (this.selectable) {
        items = items.map(
          item => ({ ...item, selected: this.selectedIdsSet.has(item[this.idField]) }),
        );
      }

      return items;
    },

    totalCount() {
      return this.$store.state[this.collectionModule].entities.items.totalCount;
    },
  },

  watch: {
    refreshKey: {
      handler() {
        if (typeof this.refreshKey !== 'undefined') {
          this.reset();
        }
      },
      immediate: true,
    },
  },

  methods: {
    ...mapActions('AlertModule', [
      'triggerErrorAlert',
      'triggerSuccessAlert',
    ]),

    async clearSearchTerm() {
      await this.handleEnterSearchTerm('');
      this.shouldFocusSearch = true;
    },

    async fetchCollection(params) {
      this.loading = true;

      try {
        await this.$store.dispatch(`${this.collectionModule}/fetchCollection`, params);
      } catch (error) {
        this.triggerErrorAlert('Unable to fetch items.');
        this.$emit('fetchCollectionError', error);
      } finally {
        this.loading = false;
      }
    },

    async handleDeleteAllFilters() {
      this.$store.dispatch(`${this.collectionModule}/deleteAllFilters`);

      await this.fetchCollection();
    },

    async handleDeleteFilters(params) {
      this.$store.dispatch(`${this.collectionModule}/deleteFilters`, params);

      await this.fetchCollection();
    },

    async handleEnterSearchTerm(searchTerm) {
      if (searchTerm.length && !searchTerm.trim().length) return;
      this.setSearchTerm({ searchTerm });

      this.shouldFocusSearch = false;

      this.resetPageNumber();

      await this.fetchCollection();
    },

    async handlePageChange(payload = {}) {
      const {
        currentPageNum = defaultPageNum,
        rowsPerPage = defaultRowsPerPage,
      } = payload;
      const {
        rowsPerPageLocalStorageKey,
      } = this;

      this.resetCollection({
        soft: true,
        preserveTotalCount: true,
      });

      this.updatePageNumber(currentPageNum, rowsPerPage);

      if (this.$options.LocalStorageService && rowsPerPageLocalStorageKey) {
        this.$options.LocalStorageService.setItem(rowsPerPageLocalStorageKey, rowsPerPage);
      }

      await this.fetchCollection();
    },

    handleSelect(selectedItem = {}) {
      const isItemSelected = this.selectedIdsSet.has((selectedItem[this.idField]));

      if (isItemSelected) {
        this.$store.dispatch(`${this.collectionModule}/unselectItem`, { id: selectedItem[this.idField] });
      } else {
        this.$store.dispatch(`${this.collectionModule}/selectItem`, { id: selectedItem[this.idField] });
      }

      this.$emit('selectItem', {
        checkedStatus: !isItemSelected,
        itemId: selectedItem[this.idField],
      });
    },

    async handleSelectAll(checkedStatus) {
      this.loading = true;

      if (checkedStatus) {
        await this.$store.dispatch(`${this.collectionModule}/selectAllItems`, { setCollection: false });
      } else {
        await this.$store.dispatch(`${this.collectionModule}/unselectAllItems`);
      }

      this.loading = false;
      this.$emit('selectAll', checkedStatus);
    },

    async handleSetFilters(params) {
      this.$store.dispatch(`${this.collectionModule}/setFilters`, params);

      await this.fetchCollection();
    },

    async handleSortColumn(payload = {}) {
      const {
        dataFieldName = this.sortBy,
        sortDirection = this.sortDirection,
      } = payload;

      const sortField = `${sortDirection === 'descending' ? '-' : ''}${dataFieldName}`;
      this.setSortField({ sortField });

      this.resetPageNumber();

      await this.fetchCollection();
    },

    async reset() {
      this.resetCollection();

      this.$store.dispatch(`${this.collectionModule}/setFilters`, { filtersArray: this.defaultFilters });

      if (this.defaultSortBy !== this.sortBy) {
        this.setSortField({ sortField: this.defaultSortBy });
      }

      this.resetPageNumber();
      await this.fetchCollection();
    },

    resetCollection(params) {
      return this.$store.dispatch(`${this.collectionModule}/resetCollection`, params);
    },

    resetPageNumber() {
      let rowsPerPage;

      if (this.$options.LocalStorageService && this.rowsPerPageLocalStorageKey) {
        rowsPerPage = this.$options.LocalStorageService.getValue(this.rowsPerPageLocalStorageKey);
      }

      this.setPage({
        pageNumber: defaultPageNum,
        limit: Number(rowsPerPage) || defaultRowsPerPage,
      });
    },

    setPage(params) {
      return this.$store.dispatch(`${this.collectionModule}/setPage`, params);
    },

    setSearchTerm(params) {
      return this.$store.dispatch(`${this.collectionModule}/setSearchTerm`, params);
    },

    setSortField(params) {
      return this.$store.dispatch(`${this.collectionModule}/setSortField`, params);
    },

    updatePageNumber(pageNumber = defaultPageNum, rowsPerPage = defaultRowsPerPage) {
      this.setPage({ pageNumber, limit: Number(rowsPerPage) });
    },
  },
};
</script>

<style module scoped>
.container {
  display: grid;
  height: 100%;
  min-height: 0;
  padding-left: var(--list-padding, var(--jc-spacer));
}
</style>
